<script setup lang="ts">
import { bemBuilder } from "@chatfood/core-utils";
import { AtomMoney, AtomText, SatIconButton } from "@chatfood/design-system";
import { computed } from "vue";
import { useModalHubStore } from "../modal-hub";
import { useLocale } from "~/composables/use-locale";
import { useDiscountsStore } from "~/stores/discounts";
import { useBasketStore } from "~/stores/basket";
import { I18nTranslate } from "~/components/i18n-translate";

const css = bemBuilder("discount-nudger");
const { t } = useLocale();
const modalHub = useModalHubStore();
const discounts = useDiscountsStore();
const basket = useBasketStore();

const promotedDiscount = computed(() => {
  const appliedDiscountId = basket.discount?.id;

  if (!appliedDiscountId) {
    return discounts.getBestAutoDiscount(basket.orderingMode);
  }

  return (
    discounts.data.find((discount) => discount.id === appliedDiscountId) ??
    discounts.getBestAutoDiscount(basket.orderingMode)
  );
});

const isAppliedState = computed(() => {
  return promotedDiscount.value?.id === basket.discount?.id;
});

const onClick = () => {
  modalHub.showDiscounts();
};
</script>

<template>
  <label :class="css('')" @click="onClick">
    <div :class="css('content')">
      <template v-if="promotedDiscount">
        <template v-if="isAppliedState">
          <AtomText
            type="body-bold"
            :class="[css('label'), css('label', 'applied')]"
            color="color-mint"
          >
            <template v-if="promotedDiscount.unit === 'percentage'">
              {{
                t(
                  "components.discounts_nudger.flag_applied_percentage_amount",
                  {
                    amount: promotedDiscount.amount,
                  },
                )
              }}
            </template>
            <I18nTranslate
              v-else
              keypath="components.discounts_nudger.flag_applied_currency_amount"
              tag="div"
            >
              <template #amount>
                <AtomMoney
                  :amount="promotedDiscount.amount.value"
                  :currency="promotedDiscount.amount.currency"
                />
              </template>
            </I18nTranslate>
          </AtomText>
        </template>
        <template v-else>
          <AtomText type="body-bold" :class="css('label')" color="color-coral">
            <template v-if="promotedDiscount.unit === 'percentage'">
              {{
                t("components.discounts_nudger.flag_percentage_amount", {
                  amount: promotedDiscount.amount,
                })
              }}
            </template>
            <I18nTranslate
              v-else
              keypath="components.discounts_nudger.flag_currency_amount"
              tag="div"
            >
              <template #amount>
                <AtomMoney
                  :amount="promotedDiscount.amount.value"
                  :currency="promotedDiscount.amount.currency"
                />
              </template>
            </I18nTranslate>
          </AtomText>
        </template>
        <AtomText tag="div" type="body-bold">
          {{ promotedDiscount.name }}
        </AtomText>
        <AtomText tag="div" type="small">
          {{ promotedDiscount.code }}
        </AtomText>
      </template>
      <template v-else>
        <AtomText tag="div" type="body-bold">
          {{ t("components.discounts_nudger.title") }}
        </AtomText>
        <AtomText tag="div" type="small">
          {{
            t(
              "components.discounts_nudger.user_phrase",
              {
                amount: discounts.data.length,
              },
              { plural: discounts.data.length },
            )
          }}
        </AtomText>
      </template>
    </div>
    <div :class="css('arrow')">
      <SatIconButton
        icon-name="caret-right-alt"
        size="extra-small"
        theme="tailored"
        variant="secondary"
      />
    </div>
  </label>
</template>

<style lang="scss" scoped>
.discount-nudger {
  border-radius: var(--radii-card-sm);
  border: 1px solid var(--color-base-90);
  cursor: pointer;
  display: flex;
  padding: var(--spacing-md);
  gap: var(--spacing-md);
  width: 100%;

  &__content {
    width: 100%;
    display: flex;
    gap: var(--spacing-sm);
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
  }

  &__label {
    padding: var(--spacing-xs) var(--spacing-sm);
    border-radius: var(--radii-card-xs);
    background-color: var(--color-salmon);

    &--applied {
      background-color: var(--color-pistachio);
    }
  }

  &__arrow {
    display: flex;
    align-items: center;
  }
}
</style>
